/**
 * Minimum spend per person step for custom request flow.
 * Executive-style large centered number with range slider and acceptance labels.
 * @module Widget/CustomRequest/steps/MinSpendStep
 */

import React, { useMemo } from 'react';
import type { CustomRequestPriceOption } from '../../lib/types.ts';
import { getAcceptanceProbability } from '../utils.ts';
import styles from '../CustomRequest.module.css';

interface MinSpendStepProps {
  priceOptions: CustomRequestPriceOption[];
  value: number;
  onChange: (spend: number) => void;
  partySize: number;
  currencySymbol: string;
}

/**
 * Spend selector that snaps to available price options.
 * Shows acceptance probability and total minimum spend.
 *
 * @param props - Component props
 * @returns Rendered minimum spend selector
 */
const MinSpendStep: React.FC<MinSpendStepProps> = ({
  priceOptions,
  value,
  onChange,
  partySize,
  currencySymbol,
}) => {
  const sortedOptions = useMemo(
    () => [...priceOptions].sort((a, b) => a.price - b.price),
    [priceOptions]
  );

  const minPrice = sortedOptions[0]?.price ?? 50;
  const maxPrice = sortedOptions[sortedOptions.length - 1]?.price ?? 500;

  const acceptance = useMemo(
    () => getAcceptanceProbability(value, priceOptions),
    [value, priceOptions]
  );

  const acceptanceColorClass =
    acceptance.color === 'text-green-600'
      ? styles.acceptanceGood
      : acceptance.color === 'text-red-600'
        ? styles.acceptanceLow
        : styles.acceptanceFair;

  const totalMinSpend = value * partySize;

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = parseInt(e.target.value, 10);
    if (sortedOptions.length > 0) {
      const closest = sortedOptions.reduce((prev, curr) =>
        Math.abs(curr.price - rawValue) < Math.abs(prev.price - rawValue) ? curr : prev
      );
      onChange(closest.price);
    } else {
      onChange(rawValue);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Large centered price */}
      <div className="flex flex-col items-center gap-2 py-2">
        <span className={styles.largeNumber}>
          {currencySymbol}{value}
        </span>
        <span className={`text-sm font-medium ${acceptanceColorClass}`}>
          {acceptance.label}
        </span>
      </div>

      {/* Slider */}
      <div className="flex flex-col gap-2 px-1">
        <input
          type="range"
          min={minPrice}
          max={maxPrice}
          value={value}
          onChange={handleSliderChange}
          className={styles.slider}
          step={1}
        />
        <div className="flex justify-between">
          <span className={styles.sectionSubtitle}>{currencySymbol}{minPrice}</span>
          <span className={styles.sectionSubtitle}>{currencySymbol}{maxPrice}</span>
        </div>
      </div>

      {/* Price option pills */}
      {sortedOptions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {sortedOptions.map((opt) => (
            <button
              key={opt.price}
              className={value === opt.price ? styles.pillSelected : styles.pill}
              onClick={() => onChange(opt.price)}
            >
              {currencySymbol}{opt.price}
            </button>
          ))}
        </div>
      )}

      {/* Total minimum spend card */}
      <div className={styles.infoCard}>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>Total minimum spend</span>
          <span className={styles.summaryValue}>
            {currencySymbol}{totalMinSpend.toLocaleString()}
          </span>
        </div>
        <p className={styles.sectionSubtitle}>
          {currencySymbol}{value} &times; {partySize} guests
        </p>
      </div>
    </div>
  );
};

export default MinSpendStep;
